import classNames from 'classnames'
import {
  type CSSProperties,
  type ChangeEvent,
  type Ref,
  forwardRef,
  useMemo,
  useImperativeHandle,
  useRef,
} from 'react'
import { Icon } from 'shared/ui/Icon'
import { getAriaLabel } from 'shared/lib'
import { type ITooltipProps, Tooltip } from 'shared/ui/Tooltip'

import styles from './styles.module.scss'

type ISizes = 16 | 20 | 32

type ICheckboxProp = {
  intent?: 'none' | 'passive'
  size?: ISizes
  isIndeterminate?: boolean
  onChange?: (value: boolean, event: ChangeEvent<HTMLInputElement>) => void
  checked?: boolean
  disabled?: boolean
  style?: CSSProperties
  ariaLabel?: string
  id?: string
  className?: string
  tooltipProps?: ITooltipProps
}

export const CheckboxRef = (
  {
    size = 20,
    intent = 'none',
    isIndeterminate = false,
    onChange,
    checked,
    style,
    disabled,
    ariaLabel,
    id,
    className,
    tooltipProps,
  }: ICheckboxProp,
  ref: Ref<HTMLInputElement>
) => {
  const inputRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [inputRef])
  const content = useMemo(() => {
    if (isIndeterminate) {
      return (
        <div className={styles.fill}>
          <Icon icon={'checkbox_mixed'} fontSize={size === 16 ? 14 : 16} />
        </div>
      )
    }
    if (checked) {
      return (
        <div className={styles.fill}>
          <Icon icon={'checkbox_checked'} fontSize={size === 16 ? 14 : 16} />
        </div>
      )
    }
    return <div className={styles.empty} />
  }, [isIndeterminate, checked])

  const checkBoxContent = (
    <span
      className={classNames(
        styles.wrap,
        {
          [styles.checked]: checked || isIndeterminate,
          [styles.disabled]: disabled,
          [styles[`intent__${intent}`]]: intent,
          [styles[`size__${size}`]]: size,
        },
        className
      )}
      style={style}
      aria-label={getAriaLabel('Checkbox', ariaLabel)}
    >
      <input
        id={id}
        type='checkbox'
        checked={checked}
        className={styles.input}
        disabled={disabled}
        onChange={(event) => onChange?.(event.target.checked, event)}
        ref={inputRef}
      />
      {content}
    </span>
  )

  return tooltipProps ? <Tooltip {...tooltipProps}>{checkBoxContent}</Tooltip> : checkBoxContent
}

export const Checkbox = forwardRef(CheckboxRef)
